<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <button class="btn btn-primary" @click="handleCreate">
                <i class="ri-add-line"></i>
                新增優惠券模板
              </button>
      </div>
    </div>

    <div class="page-container">
    <!-- 統計卡片 -->
    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-icon">
          <i class="ri-coupon-line"></i>
        </div>
        <div class="stat-content">
          <div class="stat-label">總模板數</div>
          <div class="stat-value">{{ stats.totalTemplates }}</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon success">
          <i class="ri-check-line"></i>
        </div>
        <div class="stat-content">
          <div class="stat-label">啟用中</div>
          <div class="stat-value">{{ stats.activeTemplates }}</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon info">
          <i class="ri-gift-line"></i>
        </div>
        <div class="stat-content">
          <div class="stat-label">總發放數</div>
          <div class="stat-value">{{ stats.totalIssued }}</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon warning">
          <i class="ri-checkbox-circle-line"></i>
        </div>
        <div class="stat-content">
          <div class="stat-label">總使用數</div>
          <div class="stat-value">{{ stats.totalUsed }}</div>
        </div>
      </div>
    </div>

    <div class="page-toolbar">
      <div class="search-box">
        <input
          v-model="searchQuery"
          type="text"
          class="form-control"
          placeholder="搜尋模板名稱..."
          @input="handleSearch"
        />
        <i class="ri-search-line"></i>
      </div>
      <div class="filter-group">
        <select v-model="filterCouponType" class="form-control" @change="handleFilterChange">
          <option value="">全部類型</option>
          <option value="discount">折扣券</option>
          <option value="fixed_amount">折價券</option>
          <option value="free_shipping">運費券</option>
          <option value="gift">贈品券</option>
        </select>
        <select v-model="filterStatus" class="form-control" @change="handleFilterChange">
          <option value="">全部狀態</option>
          <option value="true">啟用</option>
          <option value="false">停用</option>
        </select>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchTemplates">重試</button>
    </div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>模板名稱</th>
            <th>優惠券類型</th>
            <th>折扣值</th>
            <th>最低消費</th>
            <th>有效期</th>
            <th>發放/使用</th>
            <th>狀態</th>
            <th>建立時間</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="templates.length === 0">
            <td colspan="9" class="empty-state">
              沒有找到優惠券模板
            </td>
          </tr>
          <tr v-for="template in templates" :key="template.id">
            <td>
              <div class="template-name-cell">
                <div class="coupon-thumb-wrap">
                  <img
                    v-if="template.imageUrl"
                    :src="template.imageUrl"
                    :alt="template.templateName"
                    class="coupon-thumb"
                    loading="lazy"
                  />
                  <span v-else class="coupon-thumb-placeholder" title="未設定圖片">
                    <i class="ri-coupon-line"></i>
                  </span>
                </div>
                <strong>{{ template.templateName }}</strong>
              </div>
            </td>
            <td>
              <span :class="getCouponTypeBadgeClass(template.couponType)">
                {{ getCouponTypeText(template.couponType) }}
              </span>
            </td>
            <td>{{ formatDiscountValue(template) }}</td>
            <td>{{ formatCurrency(template.minPurchaseAmount) }}</td>
            <td>{{ formatValidityPeriod(template) }}</td>
            <td>
              <span class="stat-text">
                {{ template.totalIssued || 0 }} / {{ template.totalUsed || 0 }}
              </span>
            </td>
            <td>
              <span :class="template.isActive ? 'badge badge-success' : 'badge badge-secondary'">
                {{ template.isActive ? '啟用' : '停用' }}
              </span>
            </td>
            <td>{{ formatDate(template.createdAt) }}</td>
            <td>
              <div class="action-buttons">
                <button
                  class="btn btn-sm btn-info"
                  @click="handleView(template)"
                  title="查看詳情"
                >
                  <i class="ri-eye-line"></i>
                </button>
                <button
                  class="btn btn-sm btn-secondary"
                  @click="handleEdit(template)"
                  title="編輯"
                >
                  <i class="ri-edit-line"></i>
                </button>
                <button
                  class="btn btn-sm btn-success"
                  @click="handleBatchIssue(template)"
                  title="批次發放"
                >
                  <i class="ri-mail-send-line"></i>
                </button>
                <button
                  class="btn btn-sm btn-danger"
                  @click="handleDelete(template)"
                  title="刪除"
                >
                  <i class="ri-delete-bin-line"></i>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <ListPagination
      v-if="total > 0"
      :page="currentPage"
      :total-pages="totalPages"
      :total="total"
      @change="goToPage"
    />
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { couponService } from '@/api/modules/coupons.js';
import { showSuccess, showError, showDeleteConfirm } from '@/utils/swal.js';
import Swal from 'sweetalert2';

export default {
  name: 'CouponTemplates',
  setup() {
    const router = useRouter();
    const { pageTitle } = usePageTitle('優惠券模板列表');
    
    const templates = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const searchQuery = ref('');
    const filterCouponType = ref('');
    const filterStatus = ref('');
    const currentPage = ref(1);
    const pageSize = ref(20);
    const total = ref(0);
    const searchTimeout = ref(null);

    const stats = ref({
      totalTemplates: 0,
      activeTemplates: 0,
      totalIssued: 0,
      totalUsed: 0
    });

    const totalPages = computed(() => {
      return Math.ceil(total.value / pageSize.value);
    });

    const fetchTemplates = async () => {
      loading.value = true;
      error.value = null;

      try {
        const params = {
          page: currentPage.value,
          pageSize: pageSize.value,
        };

        if (searchQuery.value) {
          params.search = searchQuery.value;
        }

        if (filterCouponType.value) {
          params.couponType = filterCouponType.value;
        }

        if (filterStatus.value) {
          params.isActive = filterStatus.value === 'true';
        }

        const response = await couponService.getTemplates(params);

        if (response && response.success && response.data) {
          templates.value = response.data.items || [];
          total.value = response.data.pagination?.totalItems || 0;

          // 計算統計資料
          calculateStats(response.data.items || []);
        } else {
          error.value = response?.message || '獲取優惠券模板列表失敗';
        }
      } catch (err) {
        console.error('獲取優惠券模板列表失敗:', err);
        error.value = err.message || '獲取優惠券模板列表失敗';
      } finally {
        loading.value = false;
      }
    };

    const calculateStats = (items) => {
      stats.value = {
        totalTemplates: items.length,
        activeTemplates: items.filter(t => t.isActive).length,
        totalIssued: items.reduce((sum, t) => sum + (t.totalIssued || 0), 0),
        totalUsed: items.reduce((sum, t) => sum + (t.totalUsed || 0), 0)
      };
    };

    const handleSearch = () => {
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
      }
      searchTimeout.value = setTimeout(() => {
        currentPage.value = 1;
        fetchTemplates();
      }, 500);
    };

    const handleFilterChange = () => {
      currentPage.value = 1;
      fetchTemplates();
    };

    const goToPage = (page) => {
      if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page;
        fetchTemplates();
      }
    };

    const handleCreate = () => {
      router.push('/client/coupons/templates/create');
    };

    const handleView = (template) => {
      router.push(`/client/coupons/templates/${template.id}`);
    };

    const handleEdit = (template) => {
      router.push(`/client/coupons/templates/${template.id}/edit`);
    };

    const handleBatchIssue = (template) => {
      // 跳轉到批次發放頁面，預填優惠券模板 ID
      router.push({
        path: '/client/coupons/issues/batch/create',
        query: {
          templateId: template.id
        }
      });
    };

    const handleDelete = async (template) => {
      loading.value = true;
      error.value = null;

      try {
        // 先檢查是否已發送過
        const statusResponse = await couponService.checkTemplateIssuanceStatus(template.id);
        
        if (!statusResponse || !statusResponse.success) {
          showError('無法檢查發送狀態');
          return;
        }

        const issuanceStatus = statusResponse.data;
        let deleteIssues = false;

        // 如果已發送過，顯示確認對話框
        if (issuanceStatus.hasIssued) {
          const result = await Swal.fire({
            icon: 'warning',
            title: '確認刪除優惠券模板',
            html: `
              <div style="text-align: left; padding: 0 8px;">
                <p style="margin: 0 0 20px 0; font-size: 15px; color: #333;">
                  此優惠券模板「<strong style="color: #dc3545;">${template.templateName}</strong>」已發送過優惠券，共有 <strong style="color: #dc3545; font-size: 18px;">${issuanceStatus.totalIssued}</strong> 張：
                </p>
                
                <div style="background: #f8f9fa; border-radius: 8px; padding: 16px; margin: 16px 0; border: 1px solid #e9ecef;">
                  <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px;">
                    <div style="display: flex; align-items: center; gap: 8px;">
                      <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: #28a745;"></span>
                      <span style="color: #666; font-size: 14px;">已使用：</span>
                      <strong style="color: #28a745; font-size: 16px;">${issuanceStatus.totalUsed}</strong>
                      <span style="color: #999; font-size: 12px;">張</span>
                    </div>
                    <div style="display: flex; align-items: center; gap: 8px;">
                      <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: #17a2b8;"></span>
                      <span style="color: #666; font-size: 14px;">可用：</span>
                      <strong style="color: #17a2b8; font-size: 16px;">${issuanceStatus.totalAvailable}</strong>
                      <span style="color: #999; font-size: 12px;">張</span>
                    </div>
                    <div style="display: flex; align-items: center; gap: 8px;">
                      <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: #ffc107;"></span>
                      <span style="color: #666; font-size: 14px;">已過期：</span>
                      <strong style="color: #ffc107; font-size: 16px;">${issuanceStatus.totalExpired}</strong>
                      <span style="color: #999; font-size: 12px;">張</span>
                    </div>
                    <div style="display: flex; align-items: center; gap: 8px;">
                      <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: #6c757d;"></span>
                      <span style="color: #666; font-size: 14px;">已取消：</span>
                      <strong style="color: #6c757d; font-size: 16px;">${issuanceStatus.totalCancelled}</strong>
                      <span style="color: #999; font-size: 12px;">張</span>
                    </div>
                  </div>
                </div>
                
                <div style="background: #fff3cd; border-left: 4px solid #ffc107; padding: 12px 16px; margin: 20px 0; border-radius: 4px;">
                  <div style="display: flex; align-items: flex-start; gap: 8px;">
                    <span style="color: #856404; font-size: 18px; line-height: 1.2;">⚠️</span>
                    <div>
                      <p style="margin: 0; color: #856404; font-size: 14px; font-weight: 600; line-height: 1.5;">
                        重要提醒
                      </p>
                      <p style="margin: 4px 0 0 0; color: #856404; font-size: 13px; line-height: 1.5;">
                        已使用的優惠券無法移除使用紀錄，刪除後將永久無法恢復。
                      </p>
                    </div>
                  </div>
                </div>
                
                <p style="margin: 20px 0 0 0; font-size: 15px; color: #333; font-weight: 500;">
                  確定要刪除此模板及所有相關的優惠券發放紀錄嗎？
                </p>
              </div>
            `,
            showCancelButton: true,
            confirmButtonText: '是，全部刪除',
            cancelButtonText: '取消',
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            reverseButtons: true,
            width: '520px',
            padding: '24px'
          });

          if (!result.isConfirmed) {
            loading.value = false;
            return;
          }

          deleteIssues = true;
        } else {
          // 如果沒有發送過，直接確認刪除
          const confirmed = await showDeleteConfirm(template.templateName);
          
          if (!confirmed) {
            loading.value = false;
            return;
          }
        }

        // 執行刪除
        const response = await couponService.deleteTemplate(template.id, deleteIssues);

        if (response && response.success) {
          const message = deleteIssues 
            ? `刪除成功！已刪除模板及 ${response.data.deletedIssuesCount || 0} 張優惠券`
            : '刪除成功';
          showSuccess(message);
          fetchTemplates();
        } else {
          showError(response?.message || '刪除失敗');
        }
      } catch (err) {
        console.error('刪除優惠券模板失敗:', err);
        showError(err.message || '刪除失敗');
      } finally {
        loading.value = false;
      }
    };

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    };

    const formatCurrency = (amount) => {
      if (amount === null || amount === undefined) return '$0';
      return `$${Number(amount).toLocaleString('zh-TW')}`;
    };

    const formatDiscountValue = (template) => {
      if (template.couponType === 'discount') {
        return `${template.discountValue}%`;
      } else if (template.couponType === 'fixed_amount') {
        return formatCurrency(template.discountValue);
      } else if (template.couponType === 'free_shipping') {
        return '免運費';
      } else if (template.couponType === 'gift') {
        return '贈品';
      }
      return '-';
    };

    const formatValidityPeriod = (template) => {
      if (template.validityDays) {
        return `${template.validityDays} 天`;
      } else if (template.fixedStartDate && template.fixedEndDate) {
        return `${template.fixedStartDate} ~ ${template.fixedEndDate}`;
      }
      return '-';
    };

    const getCouponTypeText = (type) => {
      const typeMap = {
        discount: '折扣券',
        fixed_amount: '折價券',
        free_shipping: '運費券',
        gift: '贈品券'
      };
      return typeMap[type] || type;
    };

    const getCouponTypeBadgeClass = (type) => {
      const classMap = {
        discount: 'badge badge-primary',
        fixed_amount: 'badge badge-success',
        free_shipping: 'badge badge-info',
        gift: 'badge badge-warning'
      };
      return classMap[type] || 'badge badge-secondary';
    };

    onMounted(() => {
      fetchTemplates();
    });

    return {
      templates,
      loading,
      error,
      searchQuery,
      filterCouponType,
      filterStatus,
      currentPage,
      totalPages,
      total,
      stats,
      pageTitle,
      handleSearch,
      handleFilterChange,
      goToPage,
      handleCreate,
      handleView,
      handleEdit,
      handleBatchIssue,
      handleDelete,
      formatDate,
      formatCurrency,
      formatDiscountValue,
      formatValidityPeriod,
      getCouponTypeText,
      getCouponTypeBadgeClass,
      fetchTemplates
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_table-common.scss';
@import '~@/assets/scss/components/_list-page-common.scss';

.search-box {
  flex: 1;
  max-width: 400px;
}

.filter-group {
  display: flex;
  gap: 12px;
}

.filter-group .form-control {
  min-width: 150px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.stat-card {
  background: white;
  border-radius: 8px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f0f0;
  color: #666;
  font-size: 24px;
}

.stat-icon.success {
  background: #d4edda;
  color: #28a745;
}

.stat-icon.info {
  background: #d1ecf1;
  color: #17a2b8;
}

.stat-icon.warning {
  background: #fff3cd;
  color: #ffc107;
}

.stat-content {
  flex: 1;
}

.stat-label {
  font-size: 14px;
  color: #666;
  margin-bottom: 4px;
}

.stat-value {
  font-size: 24px;
  font-weight: 600;
  color: #333;
}

.stat-text {
  font-size: 14px;
  color: #666;
}

.table-wrapper table {
  min-width: 1400px;
}

.template-name-cell {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.coupon-thumb-wrap {
  flex-shrink: 0;
}

.coupon-thumb {
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  display: block;
}

.coupon-thumb-placeholder {
  width: 40px;
  height: 40px;
  border-radius: 6px;
  border: 1px dashed #d1d5db;
  background: #f9fafb;
  color: #9ca3af;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}
</style>
